import React from 'react';
import Alert from 'shared/components/UC/Alert';
import Button from 'shared/components/UC/Button';
import { EvaluatorApplicationDetails } from '../../types/evaluator';
import styles from './SubmissionConfirmationModal.module.css';

interface SubmissionConfirmationModalProps {
  application: EvaluatorApplicationDetails;
  evaluationSummary: {
    weightedScore: number;
    criteriaCount: number;
    hasConflicts: boolean;
  };
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
}

const SubmissionConfirmationModal: React.FC<
  SubmissionConfirmationModalProps
> = ({ application, evaluationSummary, onConfirm, onCancel, submitting }) => {
  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Confirmar Envío de Evaluación</h2>
        </div>

        <div className={styles.content}>
          <div className={styles.summarySection}>
            <h3 className={styles.sectionTitle}>Resumen de la Evaluación</h3>

            <div className={styles.applicationInfo}>
              <div className={styles.infoItem}>
                <label>Postulación:</label>
                <span>#{application.applicationNumber}</span>
              </div>
              <div className={styles.infoItem}>
                <label>Proyecto:</label>
                <span>
                  {
                    application.answers?.sectionAnswers?.PROJECT_INFORMATION
                      ?.projectTitle
                  }
                </span>
              </div>
              <div className={styles.infoItem}>
                <label>Investigador Responsable:</label>
                <span>{application?.user?.name}</span>
              </div>
            </div>

            <div className={styles.evaluationInfo}>
              <div className={styles.scoreCard}>
                <div className={styles.scoreSection}>
                  <label>Puntuación Ponderada:</label>
                  <span className={styles.score}>
                    {evaluationSummary.weightedScore.toFixed(2)} / 5.0
                  </span>
                </div>
              </div>

              <div className={styles.criteriaInfo}>
                {evaluationSummary.hasConflicts && (
                  <div className={styles.conflictsNotice}>
                    ⚠️ Se han declarado conflictos de interés
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.confirmationSection}>
            <h3 className={styles.sectionTitle}>Confirmación</h3>
            <div className={styles.confirmationList}>
              <div className={styles.confirmationItem}>
                <span className={styles.checkIcon}>✓</span>
                <span>He revisado cuidadosamente toda la evaluación</span>
              </div>
              <div className={styles.confirmationItem}>
                <span className={styles.checkIcon}>✓</span>
                <span>
                  Confirmo que las puntuaciones y comentarios son correctos
                </span>
              </div>
              <div className={styles.confirmationItem}>
                <span className={styles.checkIcon}>✓</span>
                <span>
                  Entiendo que esta evaluación no podrá ser modificada después
                  del envío
                </span>
              </div>
              <div className={styles.confirmationItem}>
                <span className={styles.checkIcon}>✓</span>
                <span>
                  He mantenido la confidencialidad y objetividad en mi
                  evaluación
                </span>
              </div>
            </div>
          </div>

          <div className={styles.finalWarning}>
            <Alert variant="warning">
              Al confirmar el envío, su evaluación será registrada como final
              y se notificará a los administradores del sistema. Esta acción
              es irreversible.
            </Alert>
          </div>
        </div>

        <div className={styles.footer}>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={submitting}
            size="default"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={submitting}
            size="default"
            className={styles.confirmButton}
          >
            {submitting ? (
              <>
                <span className={styles.spinner}>⏳</span>
                Enviando...
              </>
            ) : (
              'Confirmar y Enviar Evaluación'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubmissionConfirmationModal;
